"use client";

import React from "react";
import {
  EcommerceProductsProvider,
  useEcommerceProducts,
  AddToCartButton,
  ProductSortDropdown,
} from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";

function ProductsGridConsumer() {
  const {
    products,
    loading,
    error,
    refetch,
    currentPage,
    totalPages,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    setPage,
  } = useEcommerceProducts();

  if (loading) {
    return (
      <div className="space-y-12">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <Card key={i} data-index={i} className="overflow-hidden border-border shadow-md animate-pulse">
              <div className="aspect-square bg-muted/50" />
              <CardContent className="p-6">
                <div className="h-6 w-3/4 rounded bg-muted/50 mb-4" />
                <div className="h-4 w-1/4 rounded bg-muted/50 mb-6" />
                <div className="h-12 w-full rounded-full bg-muted/50" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="text-lg text-muted-foreground mb-4">Failed to load products.</p>
        <button
          onClick={refetch}
          className="text-accent font-semibold hover:underline"
        >
          Try again
        </button>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <p className="text-xl font-serif text-primary mb-2">No products found.</p>
        <p className="text-muted-foreground">Try adjusting your filters or search query.</p>
      </div>
    );
  }

  const renderPaginationItems = () => {
    const items = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <PaginationItem key={i} data-index={i}>
          <PaginationLink
            isActive={currentPage === i}
            onClick={(e) => {
              e.preventDefault();
              setPage(i);
            }}
            href="#"
            className={currentPage === i ? "bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground" : ""}
          >
            {i}
          </PaginationLink>
        </PaginationItem>
      );
    }
    return items;
  };

  return (
    <div className="space-y-16">
      <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {products.map((product, i) => (
          <Card key={product.id} data-index={i} className="flex flex-col overflow-hidden border-border shadow-md transition-shadow hover:shadow-lg bg-card">
            <Link to={product.link} className="block aspect-square overflow-hidden bg-muted">
              {product.featured_image_url ? (
                <img
                  src={product.featured_image_url}
                  alt={product.title}
                  className="h-full w-full object-cover transition-transform duration-300 hover:scale-105"
                  loading="lazy"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-secondary/50 text-muted-foreground">
                  No image available
                </div>
              )}
            </Link>
            
            <CardHeader className="flex-grow p-6 pb-0">
              <Link to={product.link}>
                <CardTitle className="font-serif text-2xl text-primary line-clamp-2 hover:text-accent transition-colors">
                  {product.title}
                </CardTitle>
              </Link>
              {product.short_description && (
                <div 
                  className="mt-2 text-sm text-muted-foreground line-clamp-2 font-sans"
                  dangerouslySetInnerHTML={{ __html: product.short_description }}
                />
              )}
            </CardHeader>

            <CardContent className="p-6 pt-4 pb-4">
              <div className="text-xl font-semibold text-primary">
                ${product.price?.toFixed(2) || "0.00"}
              </div>
            </CardContent>

            <CardFooter className="p-6 pt-0">
              <AddToCartButton
                productId={product.id}
                className="w-full"
                buttonClassName="bg-accent text-white hover:bg-accent/90 rounded-full py-6 text-lg font-semibold w-full transition-colors"
                text="Add to Cart"
                showQuantity={false}
              />
            </CardFooter>
          </Card>
        ))}
      </div>

      {totalPages > 1 && (
        <Pagination className="mt-12">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (hasPrev) prevPage();
                }}
                className={!hasPrev ? "pointer-events-none opacity-50" : ""}
              />
            </PaginationItem>
            
            {currentPage > 3 && totalPages > 5 && (
              <PaginationItem>
                <PaginationEllipsis />
              </PaginationItem>
            )}
            
            {renderPaginationItems()}
            
            {currentPage < totalPages - 2 && totalPages > 5 && (
              <PaginationItem>
                <PaginationEllipsis />
              </PaginationItem>
            )}

            <PaginationItem>
              <PaginationNext
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (hasNext) nextPage();
                }}
                className={!hasNext ? "pointer-events-none opacity-50" : ""}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
    </div>
  );
}

export default function ProductGridAndPagination() {
  const current_wp_query_params =
    typeof window !== "undefined" && window.wvcClient
      ? window.wvcClient.getCurrentWPQueryParams()
      : {};
  const wp_query = new WP_Query(current_wp_query_params);

  return (
    <section id="product-grid-and-pagination" className="bg-background py-24 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <EcommerceProductsProvider wp_query={wp_query} postType="product" className="products-grid-wrapper">
          <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <h2 className="text-3xl font-serif text-primary">Our Guides & Resources</h2>
            <ProductSortDropdown label="Sort by" />
          </div>
          <ProductsGridConsumer />
        </EcommerceProductsProvider>
      </div>
    </section>
  );
}